import { Decimal, ZERO, pct } from './money';

/**
 * Payout deductions — withholding tax (bracketed), service charge, and admin
 * fee applied when a member requests a cash-out. The member's wallet is still
 * debited the GROSS amount (the `payout_hold` ledger entry is unchanged); the
 * deductions are metadata that determine the NET the member actually receives.
 * The company keeps service charge + admin fee + WTAX.
 *
 * Config lives in `tenant_config.features.payout_deductions`, keyed PER PAYOUT
 * METHOD (`bank_transfer` / `gcash` / `paypal` / …) with a `default` fallback
 * for any method without an explicit entry. The math is server-authoritative
 * (`computePayoutDeductions`); the member SPA renders a preview via the API.
 *
 * Decisions (locked with the tenant):
 *  - WTAX = FLAT RATE BY BRACKET: the whole taxable amount is multiplied by the
 *    single rate of the bracket its amount falls into (not marginal/progressive).
 *  - WTAX BASE = AFTER FEES: service charge + admin fee come off the gross first,
 *    then WTAX is computed on the remainder. The bracket is also selected by that
 *    same taxable amount.
 *
 * ORDERED (CASCADING) MODE — opt-in per tenant via `payout_deductions.order`.
 * The two bullets above describe the DEFAULT, which stays the exact behaviour
 * when no order is configured. When an admin arranges the deductions in the Fees
 * & Tax editor, the array they build is applied as a cascade: each step is
 * computed on the RUNNING balance left by the steps before it, and the WTAX
 * bracket is selected by that same running balance. That's what makes the order
 * meaningful — with a percentage admin fee, "service then admin" and "admin then
 * service" produce genuinely different nets.
 *
 * Opt-in matters because cascading is NOT a superset of the default: by default
 * BOTH fees come off the gross, whereas cascading takes the second fee off
 * gross-minus-the-first. Leaving `order` unset therefore keeps every existing
 * tenant's numbers bit-for-bit unchanged.
 */

export type ChargeMode = 'percent' | 'flat';

/** A service charge / admin fee: either a percentage of gross or a flat amount. */
export type FixedCharge = {
  enabled: boolean;
  mode: ChargeMode;
  /** When `mode='percent'`, a 0–100 rate; when `mode='flat'`, a currency amount. */
  value: number;
};

/**
 * One withholding-tax bracket. `upTo` is the inclusive upper bound of the
 * taxable amount this bracket covers; `null` marks the open-ended top bracket.
 * Brackets are evaluated ascending by `upTo`.
 */
export type WtaxBracket = {
  upTo: number | null;
  /** 0–100 rate applied to the whole taxable amount when this bracket is hit. */
  percent: number;
};

export type WtaxConfig = {
  enabled: boolean;
  brackets: WtaxBracket[];
};

export type PayoutMethodDeductions = {
  service_charge: FixedCharge;
  admin_fee: FixedCharge;
  withholding_tax: WtaxConfig;
};

/**
 * A payout method the tenant offers. The list is ADMIN-MANAGED data (not
 * hardcoded) — add/rename/remove freely in the admin Fees & Tax editor. `code`
 * is the stable slug stored on `payout_requests.method`; `label` +
 * `accountLabel`/`accountPlaceholder` drive the member's request form; each
 * method carries its own deduction profile.
 */
/**
 * One admin-defined input the member fills when choosing this method (e.g. a
 * bank method asking for "Bank name" + "Account number" + "Account name"). The
 * member's value is stored under `payload[key]` on the payout request. When a
 * method has no `fields`, the legacy single `accountLabel` input is used and
 * stored under `payload.account` (back-compat).
 */
export type PayoutMethodField = {
  /** Stable key the value is stored under in `payout_requests.payload`. */
  key: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  /**
   * Optional semantic tag driving the cash-out security features:
   *  - `account_holder`: when `lock_cashout_name_to_owner` is on, the server
   *    overwrites this field with the account owner's Full Name (member can't type
   *    someone else's name). If no field is tagged, the lock is a no-op.
   *  - `account_number`: the value the `unique_cashout_account_per_user` registry
   *    keys on (one account can't be reused by a different member). Falls back to
   *    the legacy `account` key when no field is tagged.
   *  - `account_type`: the bank account kind (Savings / Current), which the
   *    disbursement export emits in its `Type` column. Untagged methods fall
   *    back to the method-level `defaultAccountType`, then to blank.
   */
  role?: 'account_holder' | 'account_number' | 'account_type';
};

/**
 * Canonicalize an account number/identifier for the unique-account registry:
 * trim, strip whitespace and `-`/`/` separators, uppercase. Deterministic so the
 * server and any preview compare identically. Non-account fields never use this.
 */
export function normalizeAccount(raw: string): string {
  return raw.replace(/[\s/-]+/g, '').trim().toUpperCase();
}

export type PayoutMethod = {
  code: string;
  label: string;
  /** Prompt for the member's account/details field (e.g. "GCash number"). */
  accountLabel: string;
  accountPlaceholder?: string;
  /** Admin-defined account inputs. Empty/absent = single `accountLabel` field. */
  fields?: PayoutMethodField[];
  /**
   * Bank/processor code the disbursement file's `Proc` column expects (e.g.
   * "BDO", "GCASH", "BPI") — whatever the tenant's bank portal uses, which is
   * why it's admin-typed rather than a hardcoded bank list. Falls back to the
   * method `code` uppercased.
   */
  procCode?: string;
  /**
   * Default for the export's `Type` column ("Savings" / "Current") when the
   * method collects no `account_type`-tagged field. Most methods only ever
   * disburse to one account kind, so a per-method default beats asking every
   * member.
   */
  defaultAccountType?: string;
  enabled: boolean;
  deductions: PayoutMethodDeductions;
};

/** The three deductions, in the order they're applied. */
export type PayoutDeductionKey = 'service_charge' | 'admin_fee' | 'withholding_tax';

/** Canonical order — also the legacy sequence, and the prefill for a new order. */
export const PAYOUT_DEDUCTION_KEYS: readonly PayoutDeductionKey[] = [
  'service_charge',
  'admin_fee',
  'withholding_tax',
];

export const PAYOUT_DEDUCTION_LABELS: Record<PayoutDeductionKey, string> = {
  service_charge: 'Service charge',
  admin_fee: 'Admin fee',
  withholding_tax: 'Withholding tax',
};

export type PayoutDeductionsConfig = {
  /** Ordered, admin-managed registry of payout methods. */
  methods: PayoutMethod[];
  /**
   * Fallback deduction profile — used for a `method` not present in `methods`
   * (defensive / legacy method strings) and as the template for new methods.
   */
  default: PayoutMethodDeductions;
  /**
   * OPTIONAL global deduction order. Absent/empty = the legacy math (both fees
   * off gross, then WTAX on the remainder). Present = cascading, each step on
   * the running balance. Deliberately ONE tenant-wide order rather than
   * per-method: a method carries its own RATES, but the sequence in which a
   * payout is whittled down is a single accounting policy.
   */
  order?: PayoutDeductionKey[];
};

/** A fully-disabled deduction profile — the safe default (zero deductions). */
export const NO_METHOD_DEDUCTIONS: PayoutMethodDeductions = {
  service_charge: { enabled: false, mode: 'percent', value: 0 },
  admin_fee: { enabled: false, mode: 'percent', value: 0 },
  withholding_tax: { enabled: false, brackets: [] },
};

/** No methods out of the box — the admin defines them. */
export const PAYOUT_DEDUCTIONS_DEFAULT: PayoutDeductionsConfig = {
  methods: [],
  default: NO_METHOD_DEDUCTIONS,
};

/** Build a blank, enabled method with zero deductions (for the admin editor). */
export function makePayoutMethod(code = '', label = ''): PayoutMethod {
  return {
    code,
    label,
    accountLabel: 'Account details',
    accountPlaceholder: '',
    fields: [],
    enabled: true,
    deductions: {
      service_charge: { enabled: false, mode: 'percent', value: 0 },
      admin_fee: { enabled: false, mode: 'percent', value: 0 },
      withholding_tax: { enabled: false, brackets: [] },
    },
  };
}

/** Resolve the deduction profile for a method code (registry entry → default). */
export function resolveMethodDeductions(
  cfg: PayoutDeductionsConfig | null | undefined,
  method: string,
): PayoutMethodDeductions {
  if (!cfg) return NO_METHOD_DEDUCTIONS;
  const methods = Array.isArray(cfg.methods) ? cfg.methods : [];
  const found = methods.find((m) => m.code === method);
  return found?.deductions ?? cfg.default ?? NO_METHOD_DEDUCTIONS;
}

function applyCharge(base: Decimal, c: FixedCharge): Decimal {
  if (!c || !c.enabled || c.value <= 0) return ZERO;
  return c.mode === 'percent' ? pct(base, c.value) : new Decimal(c.value);
}

/**
 * Flat-rate-by-bracket selection: returns the percent of the first bracket whose
 * `upTo >= taxable` (ascending), else the open-ended bracket (`upTo===null`),
 * else — defensively, if neither matches — the highest finite bracket's rate.
 */
export function selectBracketRate(taxable: Decimal, brackets: WtaxBracket[]): Decimal {
  const finite = brackets
    .filter((b) => b.upTo !== null)
    .sort((a, b) => (a.upTo as number) - (b.upTo as number));
  for (const b of finite) {
    if (taxable.lte(b.upTo as number)) return new Decimal(b.percent);
  }
  const openEnded = brackets.find((b) => b.upTo === null);
  if (openEnded) return new Decimal(openEnded.percent);
  const highest = finite[finite.length - 1];
  return highest ? new Decimal(highest.percent) : ZERO;
}

/**
 * One applied deduction, in the sequence it was applied. Every renderer (member
 * breakdown card + history line, admin request detail, merchant SPA) draws its
 * rows from here rather than hardcoding its own order — before this existed the
 * member's card and its own history line already disagreed about the sequence.
 * Synthesised for the legacy path too, so there's exactly one display contract.
 */
export type PayoutBreakdownStep = {
  key: PayoutDeductionKey;
  label: string;
  /** 'percent' for WTAX always; fees follow their configured mode. */
  mode: ChargeMode;
  /** The rate (0–100) or flat amount that produced `amount`. */
  value: Decimal;
  /** The amount this step was computed FROM (gross in legacy, running when ordered). */
  base: Decimal;
  amount: Decimal;
};

export type PayoutBreakdown = {
  gross: Decimal;
  serviceCharge: Decimal;
  adminFee: Decimal;
  /**
   * The base WTAX was computed on. Legacy: gross − serviceCharge − adminFee.
   * Ordered: the running balance immediately before the WTAX step (or the final
   * running balance when WTAX is disabled).
   */
  taxable: Decimal;
  /** The bracket rate (0–100) that was applied; 0 when WTAX is disabled. */
  withholdingTaxRate: Decimal;
  withholdingTax: Decimal;
  net: Decimal;
  /** The deductions in applied order — display reads this, not the named fields. */
  steps: PayoutBreakdownStep[];
  /** True when a cascading order was in force (drives admin/member copy). */
  ordered: boolean;
};

/**
 * Normalise a configured order into a full permutation of the three keys:
 * duplicates and unknown keys are dropped, and anything the admin left out is
 * appended in canonical order. Without the append, omitting a key from the list
 * would silently switch that deduction OFF — a config mistake that quietly
 * overpays every member. Returns null when no usable order is configured, which
 * is the signal to run the legacy math.
 */
export function resolvePayoutOrder(
  cfg: PayoutDeductionsConfig | null | undefined,
): PayoutDeductionKey[] | null {
  const raw = cfg?.order;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const seen = new Set<PayoutDeductionKey>();
  const out: PayoutDeductionKey[] = [];
  for (const k of raw) {
    if (PAYOUT_DEDUCTION_KEYS.includes(k) && !seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  if (out.length === 0) return null;
  for (const k of PAYOUT_DEDUCTION_KEYS) if (!seen.has(k)) out.push(k);
  return out;
}

/** Charge config → the (mode, value) a step reports, WTAX included. */
function stepValue(key: PayoutDeductionKey, d: PayoutMethodDeductions, rate: Decimal): { mode: ChargeMode; value: Decimal } {
  if (key === 'withholding_tax') return { mode: 'percent', value: rate };
  const c = key === 'service_charge' ? d.service_charge : d.admin_fee;
  return { mode: c?.mode ?? 'percent', value: new Decimal(c?.value ?? 0) };
}

/**
 * Compute a payout's deduction breakdown. Pure + Decimal-only.
 *
 * Default (no `order`):
 *   serviceCharge, adminFee  (each on gross)
 *   taxable = gross − serviceCharge − adminFee
 *   withholdingTax = taxable × rateOfBracketContaining(taxable)
 *   net = taxable − withholdingTax
 *
 * Ordered (`order` set): fold the steps left to right, each computed on the
 * running balance, with the WTAX bracket selected by that running balance.
 */
export function computePayoutDeductions(
  gross: Decimal,
  d: PayoutMethodDeductions,
  order?: PayoutDeductionKey[] | null,
): PayoutBreakdown {
  if (order && order.length > 0) return computeOrdered(gross, d, order);

  const serviceCharge = applyCharge(gross, d.service_charge);
  const adminFee = applyCharge(gross, d.admin_fee);
  let taxable = gross.minus(serviceCharge).minus(adminFee);
  if (taxable.lt(0)) taxable = ZERO;

  let withholdingTaxRate = ZERO;
  let withholdingTax = ZERO;
  if (d.withholding_tax?.enabled && d.withholding_tax.brackets.length > 0) {
    withholdingTaxRate = selectBracketRate(taxable, d.withholding_tax.brackets);
    withholdingTax = pct(taxable, withholdingTaxRate);
  }

  const net = taxable.minus(withholdingTax);
  // Synthesised in the legacy sequence, with each step's real base: both fees
  // off the gross, WTAX off the taxable.
  const steps: PayoutBreakdownStep[] = [];
  for (const key of PAYOUT_DEDUCTION_KEYS) {
    const amount = key === 'service_charge' ? serviceCharge : key === 'admin_fee' ? adminFee : withholdingTax;
    if (amount.lte(0)) continue;
    const { mode, value } = stepValue(key, d, withholdingTaxRate);
    steps.push({
      key,
      label: PAYOUT_DEDUCTION_LABELS[key],
      mode,
      value,
      base: key === 'withholding_tax' ? taxable : gross,
      amount,
    });
  }
  return { gross, serviceCharge, adminFee, taxable, withholdingTaxRate, withholdingTax, net, steps, ordered: false };
}

function computeOrdered(
  gross: Decimal,
  d: PayoutMethodDeductions,
  order: PayoutDeductionKey[],
): PayoutBreakdown {
  let running = gross;
  let serviceCharge = ZERO;
  let adminFee = ZERO;
  let withholdingTax = ZERO;
  let withholdingTaxRate = ZERO;
  // Legacy's `taxable` means "what WTAX was charged on". Keep that meaning:
  // the running balance the WTAX step saw, or the final balance if it never ran.
  let taxable: Decimal | null = null;
  const steps: PayoutBreakdownStep[] = [];

  for (const key of order) {
    const base = running;
    let amount = ZERO;

    if (key === 'withholding_tax') {
      taxable = base;
      if (d.withholding_tax?.enabled && d.withholding_tax.brackets.length > 0) {
        withholdingTaxRate = selectBracketRate(base, d.withholding_tax.brackets);
        amount = pct(base, withholdingTaxRate);
      }
      withholdingTax = amount;
    } else {
      amount = applyCharge(base, key === 'service_charge' ? d.service_charge : d.admin_fee);
      if (key === 'service_charge') serviceCharge = amount;
      else adminFee = amount;
    }

    // A flat charge can exceed what's left; never let the running balance go
    // negative or a later percentage step would compute off a negative base.
    running = base.minus(amount);
    if (running.lt(0)) {
      // Clamp the step to what was actually available, so Σ steps === gross − net.
      amount = base;
      running = ZERO;
      if (key === 'service_charge') serviceCharge = amount;
      else if (key === 'admin_fee') adminFee = amount;
      else withholdingTax = amount;
    }

    if (amount.gt(0)) {
      const { mode, value } = stepValue(key, d, withholdingTaxRate);
      steps.push({ key, label: PAYOUT_DEDUCTION_LABELS[key], mode, value, base, amount });
    }
  }

  return {
    gross,
    serviceCharge,
    adminFee,
    taxable: taxable ?? running,
    withholdingTaxRate,
    withholdingTax,
    net: running,
    steps,
    ordered: true,
  };
}
